import { useEffect } from "react";

const PROD_DOMAIN = "https://anterasoftware.com"; // canonical domain

function upsertMeta({ name, property, content }) {
  if (!content) return;
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    if (name) el.setAttribute("name", name);
    if (property) el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink({ rel, href }) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  let el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-jsonld", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * SEO component - sets title, meta description, canonical, OG, Twitter, and optional JSON-LD.
 * Usage: <Seo title="..." description="..." path="/solutions/distributors" />
 */
export default function Seo({
  title,
  description,
  keywords,
  path = "",
  image = `${PROD_DOMAIN}/posters/academy-intro.jpg`,
  type = "website",
  jsonLd,
}) {
  useEffect(() => {
    const url = `${PROD_DOMAIN}${path}`;
    if (title) document.title = title;
    upsertMeta({ name: "description", content: description });
    if (keywords) upsertMeta({ name: "keywords", content: keywords });
    upsertMeta({ name: "robots", content: "index, follow, max-image-preview:large" });
    upsertLink({ rel: "canonical", href: url });

    // Open Graph
    upsertMeta({ property: "og:title", content: title });
    upsertMeta({ property: "og:description", content: description });
    upsertMeta({ property: "og:url", content: url });
    upsertMeta({ property: "og:type", content: type });
    upsertMeta({ property: "og:image", content: image });
    upsertMeta({ property: "og:site_name", content: "Antera Software" });

    // Twitter
    upsertMeta({ name: "twitter:card", content: "summary_large_image" });
    upsertMeta({ name: "twitter:title", content: title });
    upsertMeta({ name: "twitter:description", content: description });
    upsertMeta({ name: "twitter:image", content: image });

    // JSON-LD
    if (jsonLd) upsertJsonLd(path || "default", jsonLd);
  }, [title, description, keywords, path, image, type, jsonLd]);

  return null;
}

// Reusable structured data builders
export const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Antera Software",
  url: PROD_DOMAIN,
  logo: "/api/media/adff924b-451a-45d0-a130-026ae966ce9a",
  description:
    "Cloud-based ERP, CRM and eCommerce software for promotional products and print businesses.",
  sameAs: [
    "https://www.youtube.com/@anterausa",
    "https://www.instagram.com/anterausa/",
    "https://www.facebook.com/Anterasoftware",
    "https://www.linkedin.com/company/antera-software",
    "https://x.com/AnteraUSA",
  ],
  address: {
    "@type": "PostalAddress",
    streetAddress: "5301 Headquarters Dr, Ste #350",
    addressLocality: "Plano",
    addressRegion: "TX",
    postalCode: "75024",
    addressCountry: "US",
  },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+1-214-556-8040",
    contactType: "sales",
    email: "sales@anterasoftware.com",
  },
};

export const softwareApplicationJsonLd = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Antera Software",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web-based, Cloud",
  description:
    "Promotional products software, CRM and eCommerce platform. ERP for promo distributors, suppliers, decorators, print shops, and sign companies with PromoStandards and QuickBooks Online integration.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5.0",
    reviewCount: "30",
  },
};

export function makeBreadcrumb(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${PROD_DOMAIN}${it.path}`,
    })),
  };
}
